/**
 * useClubAccess Hook
 *
 * Single source of truth for "can the current visitor transact at the club
 * they're viewing?" Each club has its own URL, and auth is tenant-scoped, so an
 * authenticated member can land on a sibling club in their tenant that they
 * aren't enrolled in. They may *view* it freely — but transactional CTAs
 * (booking, subscription/pass purchase) must be gated, since enrollment is
 * invite-only and booking/billing would otherwise run against a club that isn't
 * theirs.
 *
 * This hook only *decides* access; it renders nothing. Each CTA consumes it and
 * folds `'no-access'` into its own disabled state — keeping the enrolled-vs-
 * current logic here and out of the individual flows.
 *
 *   - `guest`     → not authenticated; the guest booking/sign-up flow applies.
 *   - `member`    → authenticated and enrolled at the viewed club (or still
 *                   resolving — we stay optimistic so CTAs don't flash a denial).
 *   - `no-access` → authenticated, clubs resolved, and NOT enrolled here.
 *
 * @module club/hooks
 */

import { useAuth } from '~/services/auth';
import { useClubId } from '~/services/appRuntime';
import type { EnrolledClub } from '~/services/auth';
import { useUserClubsQuery } from './useUserClubsQuery';
import { useHomeClub } from './useHomeClub';

export type ClubAccessStatus = 'guest' | 'member' | 'no-access';

/** Canonical copy shown when a member views a club they aren't enrolled in. */
export const CLUB_NO_ACCESS_LABEL = 'No access';
export const CLUB_NO_ACCESS_MESSAGE = 'Contact club to gain access to multiple facilities.';

export interface ClubAccess {
  status: ClubAccessStatus;
  /** The member's home club (null for guests / until clubs load). */
  homeClub: EnrolledClub | null;
}

export function useClubAccess(): ClubAccess {
  const { isAuthenticated } = useAuth();
  const [clubId] = useClubId();
  const { clubs, isSuccess } = useUserClubsQuery();
  const { homeClub } = useHomeClub();

  // Guests transact through the guest flow at any club.
  if (!isAuthenticated) {
    return { status: 'guest', homeClub: null };
  }

  const currentClubId = clubId != null ? Number(clubId) : NaN;

  // Until the enrolled clubs query *resolves*, stay optimistic (`member`) so a CTA
  // never flashes "No access" mid-load. `isSuccess` (not `!isLoading`) is the right
  // signal — a disabled query is pending-but-not-fetching, so `isLoading` is false
  // before the fetch even starts.
  if (!isSuccess || !Number.isFinite(currentClubId)) {
    return { status: 'member', homeClub };
  }

  // Resolved with zero enrolled clubs → an orphan account (an integrity edge the
  // home-club model otherwise prevents). Fail closed rather than leaving CTAs open.
  if (clubs.length === 0) {
    return { status: 'no-access', homeClub };
  }

  const enrolled = clubs.some(c => c.id === currentClubId);
  return { status: enrolled ? 'member' : 'no-access', homeClub };
}
